<script setup>
/**
 * WhatsApp Template Variable Parser
 * Chatwoot Design System aligned: High-visibility trigger icon + fully opaque dropdown popover.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import { isWhatsAppComplete } from '@chatwoot/utils';
import Input from 'dashboard/components-next/input/Input.vue';

import {
  buildTemplateParameters,
  allKeysRequired,
  replaceTemplateVariables,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

const { t } = useI18n();

const processedParams = ref({});
const activeMenuKey = ref(null); // Tracks open variable menu (e.g. 'body-1', 'button-0')

// Contact variables list
const contactVariables = [
  { label: 'Full Name', value: '{{contact.name}}' },
  { label: 'First Name', value: '{{contact.first_name}}' },
  { label: 'Last Name', value: '{{contact.last_name}}' },
  { label: 'Email', value: '{{contact.email}}' },
  { label: 'Phone', value: '{{contact.phone}}' },
];

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => findComponentByType(props.template, COMPONENT_TYPES.HEADER));
const bodyComponent = computed(() => findComponentByType(props.template, COMPONENT_TYPES.BODY));
const bodyText = computed(() => bodyComponent.value?.text || '');

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

const formatType = computed(() => {
  const format = headerComponent.value?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const hasVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

const renderedTemplate = computed(() => {
  return replaceTemplateVariables(bodyText.value, processedParams.value);
});

const isFormInvalid = computed(
  () => !isWhatsAppComplete(props.template, processedParams.value)
);

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const initializeTemplateParameters = () => {
  processedParams.value = buildTemplateParameters(
    props.template,
    hasMediaHeader.value
  );
};

const toggleVariableMenu = key => {
  activeMenuKey.value = activeMenuKey.value === key ? null : key;
};

const insertVariable = (variableValue, targetType, keyOrIndex) => {
  if (targetType === 'body') {
    processedParams.value.body[keyOrIndex] = variableValue;
  } else if (targetType === 'button' && processedParams.value.buttons[keyOrIndex]) {
    processedParams.value.buttons[keyOrIndex].parameter = variableValue;
  }
  activeMenuKey.value = null; // Close popover
};

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
};

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const { name, category, language, namespace } = props.template;

  const payload = {
    message: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      processed_params: processedParams.value,
    },
  };
  emit('sendMessage', payload);
};

const resetTemplate = () => emit('resetTemplate');
const goBack = () => emit('back');

onMounted(initializeTemplateParameters);

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    v$.value.$reset();
  },
  { deep: true }
);

defineExpose({
  processedParams,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  renderedTemplate,
  v$,
  updateMediaUrl,
  updateMediaName,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <!-- Live Preview Card -->
    <div class="flex flex-col gap-3 p-4 mb-4 rounded-xl border border-n-slate-3 bg-n-alpha-black2 dark:bg-n-solid-2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs font-medium text-n-slate-11 bg-n-slate-3 px-2 py-0.5 rounded-md">
          {{ languageLabel }}
        </span>
      </div>

      <div class="rounded-lg p-3 bg-n-solid-1 border border-n-slate-4">
        <div class="text-sm leading-relaxed whitespace-pre-wrap text-n-slate-12">
          {{ renderedTemplate }}
        </div>
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <!-- Inputs Section -->
    <div v-if="hasVariables || hasMediaHeader" class="space-y-4">
      <!-- Media Header Section -->
      <div v-if="hasMediaHeader">
        <p class="mb-2 text-xs font-semibold uppercase tracking-wider text-n-slate-11">
          {{
            $t('WHATSAPP_TEMPLATES.PARSER.MEDIA_HEADER_LABEL', {
              type: formatType,
            }) || `${formatType} Header`
          }}
        </p>
        <div class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_url || ''"
            type="url"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.MEDIA_URL_LABEL', {
                type: formatType,
              })
            "
            @update:model-value="updateMediaUrl"
          />
        </div>
        <div v-if="isDocumentTemplate" class="flex items-center mb-2.5">
          <Input
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            class="flex-1"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER')
            "
            @update:model-value="updateMediaName"
          />
        </div>
      </div>

      <!-- Body Variables Section -->
      <div v-if="processedParams.body">
        <p class="mb-2 text-xs font-semibold uppercase tracking-wider text-n-slate-11">
          {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
        </p>

        <div
          v-for="(variable, key) in processedParams.body"
          :key="`body-${key}`"
          class="relative flex items-center mb-3"
        >
          <Input
            v-model="processedParams.body[key]"
            type="text"
            class="w-full pr-12"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.VARIABLE_PLACEHOLDER', {
                variable: key,
              })
            "
          />

          <!-- Integrated Action Button (High Contrast Trigger) -->
          <div class="absolute right-1.5 top-1/2 -translate-y-1/2 z-10">
            <button
              type="button"
              class="flex items-center justify-center w-8 h-8 rounded-md bg-n-slate-3 text-n-slate-12 border border-n-slate-5 hover:bg-n-brand hover:text-white hover:border-n-brand transition-all shadow-sm"
              title="Insert variable"
              @click.stop="toggleVariableMenu(`body-${key}`)"
            >
              <!-- High-visibility Variable Tag Icon -->
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 stroke-[2.2]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
                <path d="M16 18l6-6-6-6" />
                <path d="M8 6L2 12l6 6" />
              </svg>
            </button>

            <!-- Opaque Solid Dropdown Popover -->
            <div
              v-if="activeMenuKey === `body-${key}`"
              class="absolute right-0 top-10 z-50 w-56 py-1.5 rounded-xl shadow-2xl bg-n-solid-3 border-2 border-n-slate-5 divide-y divide-n-slate-4 pointer-events-auto"
              @click.stop
            >
              <div class="px-3 py-1.5 text-[10px] font-bold text-n-slate-11 uppercase tracking-wider bg-n-slate-2">
                Insert Contact Variable
              </div>
              <div class="py-1">
                <button
                  v-for="item in contactVariables"
                  :key="item.value"
                  type="button"
                  class="w-full px-3 py-2 text-left text-xs font-semibold text-n-slate-12 hover:bg-n-brand hover:text-white transition-colors flex items-center justify-between group"
                  @click="insertVariable(item.value, 'body', key)"
                >
                  <span>{{ item.label }}</span>
                  <span class="text-[10px] font-mono text-n-slate-10 bg-n-solid-1 group-hover:bg-white/20 group-hover:text-white px-1.5 py-0.5 rounded border border-n-slate-4 group-hover:border-transparent">
                    {{ item.value }}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2 text-xs font-semibold uppercase tracking-wider text-n-slate-11">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>

        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="relative flex items-center mb-3"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="w-full pr-12"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />

          <!-- Integrated Action Button (High Contrast Trigger) -->
          <div class="absolute right-1.5 top-1/2 -translate-y-1/2 z-10">
            <button
              type="button"
              class="flex items-center justify-center w-8 h-8 rounded-md bg-n-slate-3 text-n-slate-12 border border-n-slate-5 hover:bg-n-brand hover:text-white hover:border-n-brand transition-all shadow-sm"
              title="Insert variable"
              @click.stop="toggleVariableMenu(`button-${index}`)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 stroke-[2.2]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
                <path d="M16 18l6-6-6-6" />
                <path d="M8 6L2 12l6 6" />
              </svg>
            </button>

            <!-- Opaque Solid Dropdown Popover -->
            <div
              v-if="activeMenuKey === `button-${index}`"
              class="absolute right-0 top-10 z-50 w-56 py-1.5 rounded-xl shadow-2xl bg-n-solid-3 border-2 border-n-slate-5 divide-y divide-n-slate-4 pointer-events-auto"
              @click.stop
            >
              <div class="px-3 py-1.5 text-[10px] font-bold text-n-slate-11 uppercase tracking-wider bg-n-slate-2">
                Insert Contact Variable
              </div>
              <div class="py-1">
                <button
                  v-for="item in contactVariables"
                  :key="item.value"
                  type="button"
                  class="w-full px-3 py-2 text-left text-xs font-semibold text-n-slate-12 hover:bg-n-brand hover:text-white transition-colors flex items-center justify-between group"
                  @click="insertVariable(item.value, 'button', index)"
                >
                  <span>{{ item.label }}</span>
                  <span class="text-[10px] font-mono text-n-slate-10 bg-n-solid-1 group-hover:bg-white/20 group-hover:text-white px-1.5 py-0.5 rounded border border-n-slate-4 group-hover:border-transparent">
                    {{ item.value }}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Validation Error -->
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-3 text-xs text-center rounded-lg bg-n-ruby-3 text-n-ruby-11 border border-n-ruby-5 font-medium"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>